import { z } from 'zod';
/**
 * Linear job state. Order matters — UI filters and pipeline gating depend on it.
 * - draft     — created, no audio yet
 * - active    — audio uploaded, items extracted, not priced
 * - priced    — pricing accepted, ready to deliver
 * - delivered — sent to client (E5 wires this); records linger for drift analysis
 * - archived  — out-of-active-rotation but kept for historical reference
 */
export const jobStatus = z.enum(['draft', 'active', 'priced', 'delivered', 'archived']);
export const createJobInput = z.object({
    business: z.string().trim().min(1, 'business is required').max(200),
    client: z.string().trim().max(200).optional(),
    address: z.string().trim().max(500).optional(),
    notes: z.string().trim().max(2000).optional(),
});
export const updateJobInput = createJobInput
    .partial()
    .extend({
    status: jobStatus.optional(),
    assignedTo: z.string().trim().nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'no updates supplied' });
export const jobSchema = z.object({
    id: z.string().min(1),
    business: z.string().min(1),
    client: z.string().optional(),
    address: z.string().optional(),
    status: jobStatus,
    /** Firebase Auth uid of the user this job is assigned to. Null = unassigned. */
    assignedTo: z.string().nullable(),
    /** Firebase Auth uid of the user who created the job. */
    createdBy: z.string().min(1),
    notes: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const listJobsQuery = z.object({
    status: jobStatus.optional(),
    /** Case-insensitive substring match on business OR client. */
    q: z.string().trim().max(200).optional(),
    assignedTo: z.string().trim().optional(),
});
// ---------------------------------------------------------------------------
// Transcriptions — STT runs attached to a job. One job can have multiple.
// ---------------------------------------------------------------------------
/** Storage pointers for a single STT run, as captured from the engine's `archived` payload. */
export const transcriptionArchive = z.object({
    audio: z.string().optional(),
    transcript: z.string().optional(),
    items: z.string().optional(),
    excel: z.string().optional(),
    run_log: z.string().optional(),
});
export const transcription = z.object({
    /** STT engine task id, used as the dedupe key. */
    sttTaskId: z.string().min(1),
    /** Source filename(s) submitted to the engine. */
    filenames: z.array(z.string()).default([]),
    /** Greg's chosen template at dictation time (going_concern | liquidation). */
    template: z.string().optional(),
    /** GCS URIs of artifacts archived by the STT engine. */
    archived: transcriptionArchive,
    /** Number of items extracted (cached for the UI; authoritative count is the items subcollection). */
    itemCount: z.number().int().nonnegative(),
    addedAt: z.string(),
    addedBy: z.string().min(1),
});
export const attachTranscriptionInput = z.object({
    sttTaskId: z.string().trim().min(1, 'sttTaskId is required'),
    template: z.string().trim().optional(),
});
// ---------------------------------------------------------------------------
// Pricing v2 engine payload — the `PricingResultV2` shape emitted by the STT
// engine (stt-service/pipeline/pricing_v2/schema.py). Persisted verbatim as
// `JobItem.pricingDecision`. The engine emits `*_label` display strings so the
// UI never hardcodes an enum->text map (engine decision D23); enum fields are
// therefore typed as plain strings here. `.passthrough()` keeps the schema
// forward-compatible if the engine adds fields.
// ---------------------------------------------------------------------------
export const pricingCandidate = z
    .object({
    product_name: z.string(),
    price_cad: z.number().nullable().optional(),
    price_original: z.number().nullable().optional(),
    currency: z.string().default('CAD'),
    url: z.string().nullable().optional(),
    supplier: z.string(),
    domain: z.string(),
    /** Rerank classification: 'exact' | 'similar' | 'other'. */
    classification: z.string().default('other'),
})
    .passthrough();
/** The engine-owned valuation ladder, all CAD. v2 owns depreciation — the
 *  portal reads these, it never derives GC/OLV/FLV itself. */
export const pricingValuation = z
    .object({
    rv: z.number().nullable().optional(),
    gc: z.number().nullable().optional(),
    olv: z.number().nullable().optional(),
    flv: z.number().nullable().optional(),
})
    .passthrough();
/** A flagged likely dictation error — `from` was corrected `to`. */
export const pricingCorrection = z
    .object({
    field: z.string(),
    from: z.string(),
    to: z.string(),
    reason: z.string(),
})
    .passthrough();
export const pricingChosenSource = z
    .object({
    url: z.string().nullable().optional(),
    supplier: z.string(),
    product_name: z.string(),
    price_cad: z.number().nullable().optional(),
    price_original: z.number().nullable().optional(),
    currency: z.string().default('CAD'),
})
    .passthrough();
/** Plausibility band of the engine price vs Greg's dictated value. */
export const pricingGregValueCheck = z
    .object({
    greg_value: z.number().nullable().optional(),
    engine_rv: z.number().nullable().optional(),
    ratio: z.number().nullable().optional(),
    /** 'plausible' | 'suspect_low' | 'suspect_high' | 'no_signal'. */
    verdict: z.string().default('no_signal'),
})
    .passthrough();
/** One supplier that could not be retrieved (transient failure). */
export const pricingSourceFailure = z
    .object({
    supplier: z.string(),
    domain: z.string(),
    /** 'http_503' | 'http_429' | 'http_502' | 'http_504' | 'timeout' | 'transport_error'. */
    reason: z.string(),
})
    .passthrough();
/** Per-item retrieval-source health — drives the degraded banner. A price
 *  computed off a partial pool must disclose it (audit-trail rule). */
export const pricingRetrievalHealth = z
    .object({
    sources_attempted: z.number().default(0),
    sources_ok: z.number().default(0),
    sources_failed: z.array(pricingSourceFailure).default([]),
    degraded: z.boolean().default(false),
})
    .passthrough();
export const pricingWalkIn = z
    .object({
    is_walk_in: z.boolean().default(false),
    dimensions: z.string().nullable().optional(),
    sqft: z.number().nullable().optional(),
    type: z.string().nullable().optional(),
})
    .passthrough();
export const pricingShortCircuit = z
    .object({
    fired: z.boolean().default(false),
    reason: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
})
    .passthrough();
export const pricingWebDiscovery = z
    .object({
    searched: z.array(z.string()).default([]),
    urls_triaged: z.array(z.string()).default([]),
    urls_fetched: z.array(z.string()).default([]),
})
    .passthrough();
export const pricingDepreciation = z
    .object({ multiplier: z.number(), basis: z.string() })
    .passthrough();
export const pricingCost = z
    .object({
    usd: z.number().default(0),
    breakdown: z.record(z.number()).default({}),
})
    .passthrough();
export const pricingErrorInfo = z
    .object({ kind: z.string(), message: z.string() })
    .passthrough();
export const pricingResultV2 = z
    .object({
    schema_version: z.string().optional(),
    engine: z.string().optional(),
    // identity & lifecycle
    job_id: z.string(),
    client_id: z.string().nullable().optional(),
    description: z.string(),
    greg_value: z.number().nullable().optional(),
    /** queued|retrieving|reranking|correcting|web_discovery|estimating|done|error|cancelled */
    stage: z.string(),
    stage_label: z.string().optional(),
    is_done: z.boolean().optional(),
    is_cancelled: z.boolean().optional(),
    created_at: z.number().optional(),
    completed_at: z.number().nullable().optional(),
    elapsed_s: z.number().nullable().optional(),
    // result
    valuation: pricingValuation.default({}),
    depreciation: pricingDepreciation.nullable().optional(),
    /** historical|exact_model|same_product|estimate_from_comparables|estimate_weak|no_price */
    confidence: z.string(),
    confidence_label: z.string().optional(),
    needs_review: z.boolean().optional(),
    review_reason: z.string().nullable().optional(),
    /** historical|pass1_rerank|pass2_corrected|web_discovery|estimation */
    pipeline_path: z.string().nullable().optional(),
    pipeline_path_label: z.string().nullable().optional(),
    chosen_source: pricingChosenSource.nullable().optional(),
    match_reason: z.string().nullable().optional(),
    // evidence & audit
    candidate_pool: z.array(pricingCandidate).default([]),
    corrections_applied: z.array(pricingCorrection).default([]),
    greg_value_check: pricingGregValueCheck.default({}),
    short_circuit: pricingShortCircuit.optional(),
    web_discovery: pricingWebDiscovery.nullable().optional(),
    walk_in: pricingWalkIn.default({}),
    retrieval_health: pricingRetrievalHealth.default({}),
    // diagnostics
    cost: pricingCost.optional(),
    timing: z.record(z.number()).default({}),
    error: pricingErrorInfo.nullable().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Line items — one Firestore doc per item in /jobs/{id}/items/{itemId}.
// Mirrors the STT engine's item shape with provenance fields added.
// ---------------------------------------------------------------------------
export const jobItem = z.object({
    id: z.string().min(1),
    /** Which transcription run produced this item. */
    sourceTaskId: z.string().min(1),
    /** Stable index within that run, for ordering. */
    sortOrder: z.number().int().nonnegative(),
    // The engine emits two shapes: { type: 'header', text } and
    // { type: 'asset', description, qty, value, sheet? }. Brand + model live
    // inside description (e.g. "True T-72F stainless steel 3-door reach-in
    // freezer") — no separate fields.
    qty: z.number().nullable().optional(),
    description: z.string().optional(),
    isHeader: z.boolean().optional(),
    isLeasehold: z.boolean().optional(),
    /** Per-item value as Greg called it during dictation (engine's `value` field). */
    greg_value: z.number().nullable().optional(),
    /** Free-form engine payload; everything not pulled into typed fields lands here. */
    raw: z.record(z.unknown()).optional(),
    createdAt: z.string(),
    // --- Pricing fields (v2 engine — INTEGRATION_SPEC WI-5) ---
    /** Engine replacement value (valuation.rv, CAD) — the new-retail price. */
    suggestedPrice: z.number().nullable().optional(),
    /** Engine going-concern value (valuation.gc, CAD). The engine owns the
     *  RV->GC depreciation; the portal reads it, it never derives it. */
    suggestedGc: z.number().nullable().optional(),
    /** Engine match confidence (v2 Confidence enum string): historical |
     *  exact_model | same_product | estimate_from_comparables | estimate_weak |
     *  no_price. */
    suggestionConfidence: z.string().nullable().optional(),
    /** How the price was reached (v2 PipelinePath enum string): historical |
     *  pass1_rerank | pass2_corrected | web_discovery | estimation. */
    suggestionTier: z.string().nullable().optional(),
    /** Engine's needs-review flag — true items wait for Greg, never auto-accept. */
    needsReview: z.boolean().nullable().optional(),
    /** Final going-concern price Greg accepted (auto-accepted = valuation.gc,
     *  or a manual override). null until accepted. */
    acceptedPrice: z.number().nullable().optional(),
    /** True when acceptedPrice differs from the engine's suggested GC. */
    priceOverridden: z.boolean().optional(),
    /** When the price was accepted/overridden. */
    acceptedAt: z.string().nullable().optional(),
    /** uid of the user who accepted/overridden ('auto' for auto-accept). */
    acceptedBy: z.string().nullable().optional(),
    /** Which pricing batch produced the suggestion (for re-pricing). */
    pricingBatchId: z.string().nullable().optional(),
    /** Full engine pricing payload (PricingResultV2) — evidence panel source. */
    pricingDecision: pricingResultV2.nullable().optional(),
});
// ---------------------------------------------------------------------------
// Pricing batches — one per "Price all" or "Re-price" click.
// ---------------------------------------------------------------------------
export const pricingBatchStatus = z.enum(['running', 'complete', 'cancelled', 'error']);
export const pricingBatch = z.object({
    /** Portal-generated batch id (UUID). Stable across re-polls. */
    batchId: z.string().min(1),
    /** Engine-side batch id returned by /v2/pricing/search. */
    sttBatchId: z.string().min(1),
    /** Mapping of portal itemId -> engine job_id (set at start). */
    itemMapping: z.record(z.string()).default({}),
    itemCount: z.number().int().nonnegative(),
    status: pricingBatchStatus,
    startedAt: z.string(),
    completedAt: z.string().nullable().optional(),
    startedBy: z.string().min(1),
});
export const startPricingBatchInput = z.object({
    /** Subset of jobItem.ids to price. Omitted = all unpriced asset items. */
    itemIds: z.array(z.string().min(1)).optional(),
});
export const acceptPriceInput = z.object({
    /** null = un-accept (clears acceptedPrice). */
    acceptedPrice: z.number().nullable(),
});
/** Dictation-field edit on an existing line item. Each field is optional
 *  so the UI can autosave a single cell on blur. null clears greg_value /
 *  qty back to "not set". */
export const editItemInput = z
    .object({
    description: z.string().trim().min(1).max(500).optional(),
    qty: z.number().int().min(0).max(99999).nullable().optional(),
    greg_value: z.number().min(0).max(10000000).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'no updates supplied' });
